import {
	Component,
	inject,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	ChangeDetectionStrategy,
} from '@angular/core';

import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { SendEmailFormComponent } from './send-email-form.component';
import {
	MatTableDataSource,
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { ApplicationEmail } from '@lmc-app/shared/interface/application-email.interface';
import { Applicant } from '@lmc-app/shared/enum/applicant.enum';
import { DateUtil } from '@lmc-app/shared/utils/date.util';
import { EmailViewDialogComponent } from '@lmc-app/summary/activities/email/email-view-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationEmailStatus } from '@lmc-app/shared/enum/application-email-status.enum';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { StateDisplayComponent } from '../../../shared/components/state-display/state-display.component';

@Component({
	selector: 'app-email',
	templateUrl: './email.component.html',
	styleUrls: ['./email.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [],
	imports: [
		MatCard,
		MatCardContent,
		MatButton,
		NgIf,
		MatIcon,
		StateDisplayComponent,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
	],
})
export class EmailComponent implements OnInit, OnChanges {
	@Input() application: any;

	displayedColumnsEmails: string[] = [
		'applicant',
		'subject',
		'sender',
		'recipient',
		'dateTime',
		'status',
		'action',
	];

	dataSourceEmails = new MatTableDataSource<ApplicationEmail>();

	// Loading states
	isLoading = true;
	hasError = false;
	errorMessage = '';

	protected readonly Applicant = Applicant;
	protected readonly DateUtil = DateUtil;
	protected readonly ApplicationEmailStatus = ApplicationEmailStatus;

	private _bottomSheet = inject(MatBottomSheet);
	private _dialog = inject(MatDialog);

	constructor(private apiService: LegacyApiService) {}

	ngOnInit() {}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.application) {
			this.application = changes.application?.currentValue;
			if (this.application) {
				this.getEmails();
			}
		}
	}

	getEmails() {
		this.isLoading = true;
		this.hasError = false;
		this.errorMessage = '';

		this.apiService.getEmailsByApplicationId(this.application.id).subscribe({
			next: (res) => {
				this.dataSourceEmails.data = res || [];
				this.isLoading = false;
			},
			error: (error) => {
				console.error(error);
				this.hasError = true;
				this.errorMessage = 'Failed to load emails';
				this.isLoading = false;
			},
		});
	}

	openSendEmailBottomSheet() {
		this._bottomSheet
			.open(SendEmailFormComponent, {
				data: { application: this.application },
			})
			.afterDismissed()
			.subscribe((data) => {
				if (data) {
					this.dataSourceEmails.data = data;
				}
			});
	}

	viewEmail(email: ApplicationEmail) {
		const dialogRef = this._dialog.open(EmailViewDialogComponent, {
			width: '70%',
			maxWidth: '900px',
			height: '80vh',
			maxHeight: '80vh',
			data: { email: email },
			panelClass: 'email-view-dialog',
			disableClose: false,
		});

		dialogRef.afterClosed().subscribe((result) => {
			console.log('Email dialog closed');
		});
	}

	// Getter methods for template
	get showStateDisplay(): boolean {
		return (
			this.isLoading || this.hasError || this.dataSourceEmails.data.length === 0
		);
	}

	get stateType(): 'loading' | 'empty' | 'error' {
		if (this.isLoading) return 'loading';
		if (this.hasError) return 'error';
		return 'empty';
	}

	get showTable(): boolean {
		return (
			!this.isLoading && !this.hasError && this.dataSourceEmails.data.length > 0
		);
	}
}
